import React, { useState, useEffect } from 'react';
import { X, Search, Trash2, Download, Calendar } from 'lucide-react';
import { savedAnalysesService } from '../../services/savedAnalyses';
import { SavedAnalysis } from '../../types/user';
import { CalculatorParams } from '../../types/api';
import { useAuth } from '../../hooks/useAuth';
import { UpgradePrompt } from '../Subscription/UpgradePrompt';
import { debugEnabled, dlog } from '../../utils/debug';

interface SavedAnalysesListProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadAnalysis: (analysis: SavedAnalysis) => void;
}

export const SavedAnalysesList: React.FC<SavedAnalysesListProps> = ({
  isOpen,
  onClose,
  onLoadAnalysis
}) => {
  const { user, subscriptionStatus } = useAuth();
  const [analyses, setAnalyses] = useState<SavedAnalysis[]>([]);
  const [filteredAnalyses, setFilteredAnalyses] = useState<SavedAnalysis[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Load analyses when modal opens (only for subscribed users)
  useEffect(() => {
    if (isOpen && subscriptionStatus?.has_active_subscription) {
      loadAnalyses();
    }
  }, [isOpen, subscriptionStatus]);

  // Filter analyses based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredAnalyses(analyses);
    } else {
      const filtered = analyses.filter(analysis =>
        analysis.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        analysis.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredAnalyses(filtered);
    }
  }, [analyses, searchQuery]);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);
      const userAnalyses = await savedAnalysesService.getUserAnalyses();
      setAnalyses(userAnalyses);
    } catch (error) {
      console.error('Failed to load analyses:', error);
      setError('Failed to load saved analyses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

          const handleLoadAnalysis = (analysis: SavedAnalysis) => {
            try {
              if (debugEnabled) {
                dlog('SavedAnalysesList: Loading analysis:', analysis);
                dlog('SavedAnalysesList: Analysis parameters:', analysis.parameters);
                dlog('SavedAnalysesList: Analysis title:', analysis.title);
              }
              onLoadAnalysis(analysis);
              onClose();
            } catch (error) {
              console.error('Failed to load analysis:', error);
              setError('Failed to load analysis. Please try again.');
            }
          };

  const handleDeleteAnalysis = async (analysisId: string) => {
    if (!window.confirm('Are you sure you want to delete this analysis?')) {
      return;
    }

    try {
      setDeletingId(analysisId);
      await savedAnalysesService.deleteAnalysis(analysisId);
      setAnalyses(prev => prev.filter(a => a.id !== analysisId));
    } catch (error) {
      console.error('Failed to delete analysis:', error);
      setError('Failed to delete analysis. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Saved Analyses</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6 flex-1 overflow-hidden flex flex-col">
          {/* Show upgrade prompt for free users */}
          {!subscriptionStatus?.has_active_subscription ? (
            <div className="flex-1 flex items-center justify-center">
              <UpgradePrompt 
                isOpen={true}
                onClose={onClose}
                requiresAuth={!user}
              />
            </div>
          ) : (
            <>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search analyses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : filteredAnalyses.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      {searchQuery ? 'No analyses match your search.' : 'No saved analyses yet.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredAnalyses.map((analysis) => (
                      <div
                        key={analysis.id}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-medium text-gray-900 truncate">
                              {analysis.title}
                            </h3>
                            {analysis.description && (
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {analysis.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(analysis.created_at)}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => handleLoadAnalysis(analysis)}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                            >
                              Load
                            </button>
                            <button
                              onClick={() => handleDeleteAnalysis(analysis.id)}
                              disabled={deletingId === analysis.id}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            >
                              {deletingId === analysis.id ? (
                                <div className="w-4 h-4 animate-spin rounded-full border-b-2 border-red-600"></div>
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
